<script setup lang="ts">
/**
 * Module: web/src/components/PinPad.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * 4-digit PIN pad -- mobile-first with LARGE tap targets (doc 12 sect 4.1).
 * Emits 'complete' when the 4th digit lands; parent decides when to submit.
 */
import { computed } from 'vue';
import AppIcon from '@/components/AppIcon.vue';

const props = defineProps<{
  modelValue: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'complete', value: string): void;
}>();

const PIN_LENGTH = 4;
const digits = computed(() => props.modelValue.split(''));

function press(d: string) {
  if (props.disabled || props.modelValue.length >= PIN_LENGTH) return;
  const next = props.modelValue + d;
  emit('update:modelValue', next);
  if (next.length === PIN_LENGTH) emit('complete', next);
}

function backspace() {
  if (props.disabled || props.modelValue.length === 0) return;
  emit('update:modelValue', props.modelValue.slice(0, -1));
}

function clearAll() {
  if (props.disabled) return;
  emit('update:modelValue', '');
}
</script>

<template>
  <div>
    <!-- Dots -->
    <div class="flex items-center justify-center gap-4 mb-5" aria-label="PIN entry progress">
      <span
        v-for="i in PIN_LENGTH"
        :key="i"
        class="w-4 h-4 rounded-full border-2 transition-colors"
        :class="digits.length >= i ? 'bg-accent border-accent' : 'border-border-c'"
      />
    </div>

    <!-- Pad: large 64px+ buttons -->
    <div class="grid grid-cols-3 gap-2.5 max-w-xs mx-auto">
      <button
        v-for="d in ['1', '2', '3', '4', '5', '6', '7', '8', '9']"
        :key="d"
        type="button"
        class="h-16 rounded-xl bg-input border border-border-c text-2xl font-semibold text-fg
          active:bg-accent/20 hover:bg-card transition-colors disabled:opacity-40"
        :disabled="disabled"
        @click="press(d)"
      >
        {{ d }}
      </button>
      <button
        type="button"
        class="h-16 rounded-xl border border-border-c text-sm font-medium text-fg-muted
          active:bg-accent/20 hover:bg-card transition-colors disabled:opacity-40"
        :disabled="disabled"
        @click="clearAll"
      >
        Clear
      </button>
      <button
        type="button"
        class="h-16 rounded-xl bg-input border border-border-c text-2xl font-semibold text-fg
          active:bg-accent/20 hover:bg-card transition-colors disabled:opacity-40"
        :disabled="disabled"
        @click="press('0')"
      >
        0
      </button>
      <button
        type="button"
        class="h-16 rounded-xl border border-border-c text-fg-muted flex items-center justify-center
          active:bg-accent/20 hover:bg-card transition-colors disabled:opacity-40"
        aria-label="Backspace"
        :disabled="disabled"
        @click="backspace"
      >
        <AppIcon name="back" class="w-6 h-6" />
      </button>
    </div>
  </div>
</template>
